import React from "react";
import {
    Area,
    AreaChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import type { ChartDataPoint } from "../data/chartData";

const ACCENT = "#FD4017";

export interface ChartAreaProps {
    title: string;
    data: ChartDataPoint[];
    formatter: (value: number) => string;
}

export function ChartArea({
    title,
    data,
    formatter
}: ChartAreaProps): React.ReactElement {
    const color = ACCENT;
    const gradientId = `gradient-${title.replace(/[^a-zA-Z0-9]/g, "-")}`;
    const tickInterval = Math.max(0, Math.ceil(data.length / 6) - 1);
    return (
        <div className="h-[200px] sm:h-60 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={data}
                    margin={{ top: 4, right: 24, left: 4, bottom: 0 }}
                >
                    <defs>
                        <linearGradient
                            id={gradientId}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={color}
                                stopOpacity={0.5}
                            />
                            <stop
                                offset="100%"
                                stopColor={color}
                                stopOpacity={0.5}
                            />
                        </linearGradient>
                    </defs>
                    <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#1B1C20" }}
                        interval={tickInterval}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#1B1C20" }}
                        tickFormatter={formatter}
                        width={60}
                    />
                    <Tooltip
                        content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                                <div
                                    style={{
                                        backgroundColor: "rgba(255,255,255,0.95)",
                                        border: "1px solid #e5e7eb",
                                        padding: "8px 12px",
                                        fontSize: "13px"
                                    }}
                                >
                                    <p style={{ margin: 0, color: "#1B1C20" }}>{label}</p>
                                    <p style={{ margin: 0, color: ACCENT, fontWeight: 600 }}>
                                        {formatter(payload[0].value as number)}
                                    </p>
                                </div>
                            );
                        }}
                    />
                    <Area
                        type="linear"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={4}
                        fill={`url(#${gradientId})`}
                        animationDuration={1500}
                        animationEasing="ease-out"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}
